import { useEffect, useRef, useState } from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiList,
  FiMapPin,
  FiX,
  FiSend,
  FiZap,
  FiPlus,
  FiClock,
  FiFileText,
  FiCalendar,
  FiClipboard,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};


function ShortJobs() {
  const navigate = useNavigate();

  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  const clientId = getClientId();

  const [formData, setFormData] = useState({
    role: "",
    startTime: "",
    endTime: "",
    totalDuration: "",
    salary: "",
    experience: "",
    location: "",
    department: "",
    description: "",
    skills: "",
    jobType: "short",
    companyName: "",
    aboutCompany: "",
    expiryDate: "",
    startDate: "",
    endDate: "",
    startPeriod: "AM",
    endPeriod: "PM",
  });

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const roleRef = useRef(null);
  const deptRef = useRef(null);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [deptForm, setDeptForm] = useState({ name: "", description: "" });
  const [modalLoading, setModalLoading] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [experienceOptions, setExperienceOptions] = useState([]);
  const [showExpModal, setShowExpModal] = useState(false);
  const [newExpLabel, setNewExpLabel] = useState("");
  const [showExpDropdown, setShowExpDropdown] = useState(false);
  const expDropdownRef = useRef(null);
  const [dateErrors, setDateErrors] = useState({ startDate: "", endDate: "" });
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleRef.current && !roleRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
      if (deptRef.current && !deptRef.current.contains(event.target)) {
        setShowDeptDropdown(false);
      }
      if (expDropdownRef.current && !expDropdownRef.current.contains(event.target)) {
        setShowExpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchDepartments();
    fetchEmployees();
    fetchExperienceOptions();
    fetchCompanyDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanyDefaults = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/settings?clientId=${clientId}`);
      if (res.data.success && res.data.settings) {
        const { companyName, aboutCompany } = res.data.settings;
        setFormData(prev => ({
          ...prev,
          companyName: companyName || "Timely Health",
          aboutCompany: aboutCompany || "",
        }));
      }
    } catch (err) {
      console.error("Failed to fetch company defaults:", err);
    }
  };

  // Automatic Duration Calculation
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      let [startH, startM] = formData.startTime.split(":").map(Number);
      let [endH, endM] = formData.endTime.split(":").map(Number);

      if (formData.startPeriod === "PM" && startH < 12) startH += 12;
      if (formData.startPeriod === "AM" && startH === 12) startH = 0;
      if (formData.endPeriod === "PM" && endH < 12) endH += 12;
      if (formData.endPeriod === "AM" && endH === 12) endH = 0;

      let startTotal = startH * 60 + startM;
      let endTotal = endH * 60 + endM;

      let diff = endTotal - startTotal;
      if (diff < 0) diff += 24 * 60;

      const hours = diff / 60;
      const formattedDuration = `${hours.toFixed(1).replace(/\.0$/, "")} Hours`;

      setFormData((prev) => ({ ...prev, totalDuration: formattedDuration }));
    }
  }, [formData.startTime, formData.endTime, formData.startPeriod, formData.endPeriod]);

  const fetchRoles = async () => {
    try {
      const id = getClientId();
      const url = id
        ? `${API_BASE_URL}/roles/all?clientId=${id}`
        : `${API_BASE_URL}/roles/all`;
      const res = await axios.get(url);
      if (res.data.success || Array.isArray(res.data) || res.data.data) {
        let rolesData = Array.isArray(res.data) ? res.data : (res.data.data || res.data.roles || []);
        const uniqueRoles = rolesData.map((r, i) => ({ _id: r._id || i, name: r.name || r.title || r.role })).filter(r => r.name);
        setRoles(uniqueRoles);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const id = getClientId();
      const url = id
        ? `${API_BASE_URL}/department/all?clientId=${id}`
        : `${API_BASE_URL}/department/all`;
      const res = await axios.get(url);
      if (res.data.success || Array.isArray(res.data) || res.data.data) {
        let deptData = [];
        if (Array.isArray(res.data)) deptData = res.data;
        else if (Array.isArray(res.data.data)) deptData = res.data.data;
        else if (Array.isArray(res.data.departments)) deptData = res.data.departments;

        const uniqueDepts = deptData.map((d, index) => {
          if (typeof d === 'string') return { _id: index, name: d };
          return { _id: d._id || index, name: d.name || d.departmentName || "Unknown Dept" };
        }).filter(d => d.name !== "Unknown Dept");

        setDepartments(uniqueDepts);
      }
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const id = getClientId();
      const url = id
        ? `${API_BASE_URL}/employees/get-employees?clientId=${id}`
        : `${API_BASE_URL}/employees/get-employees`;
      const res = await axios.get(url);
      const empData = Array.isArray(res.data) ? res.data : res.data.employees || res.data.data || [];
      setEmployees(empData);
    } catch (err) {
      console.error("Failed to fetch employees");
    }
  };

  useEffect(() => {
    if (employees.length > 0) {
      const employeeRoles = [...new Set(employees.map(emp => (emp.role || emp.position || emp.designation || "").trim()).filter(Boolean))];
      setRoles(prev => {
        const existingNames = new Set(prev.map(r => r.name.toLowerCase()));
        const newRoles = [...prev];
        employeeRoles.forEach((roleName, index) => {
          if (!existingNames.has(roleName.toLowerCase())) {
            newRoles.push({ _id: `emp-role-${index}`, name: roleName });
          }
        });
        return newRoles.sort((a, b) => a.name.localeCompare(b.name));
      });

      const employeeDepts = [...new Set(employees.map(emp => (emp.department || emp.dept || "").trim()).filter(Boolean))];
      setDepartments(prev => {
        const existingNames = new Set(prev.map(d => d.name.toLowerCase()));
        const newDepts = [...prev];
        employeeDepts.forEach((deptName, index) => {
          if (!existingNames.has(deptName.toLowerCase())) {
            newDepts.push({ _id: `emp-dept-${index}`, name: deptName });
          }
        });
        return newDepts.sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  }, [employees]);

  const handleCreateRole = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/roles/create`, { ...roleForm, clientId });
      if (response.data.success) {
        setMessage({ type: "success", text: `Role "${roleForm.name}" created!` });
        await fetchRoles();
        setFormData(prev => ({ ...prev, role: roleForm.name }));
        setShowRoleModal(false);
        setRoleForm({ name: "", description: "" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to create role" });
    } finally {
      setModalLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/department/create`, { ...deptForm, clientId });
      if (response.data.success) {
        setMessage({ type: "success", text: `Department "${deptForm.name}" created!` });
        await fetchDepartments();
        setFormData(prev => ({ ...prev, department: deptForm.name }));
        setShowDeptModal(false);
        setDeptForm({ name: "", description: "" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to create department" });
    } finally {
      setModalLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const fetchExperienceOptions = async () => {
    try {
      const id = getClientId();
      const url = id
        ? `${API_BASE_URL}/experience-options/all?clientId=${id}`
        : `${API_BASE_URL}/experience-options/all`;
      const res = await axios.get(url);
      if (res.data.success) {
        setExperienceOptions(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch experience options:", err);
    }
  };

  const handleCreateExperience = async (e) => {
    e.preventDefault();
    if (!newExpLabel) return;
    setModalLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/experience-options/create`, { label: newExpLabel, clientId });
      if (res.data.success) {
        setExperienceOptions(prev => [res.data.data, ...prev]);
        setFormData(prev => ({ ...prev, experience: res.data.data.label }));
        setNewExpLabel("");
        setShowExpModal(false);
        setShowExpDropdown(false);
        setMessage({ type: "success", text: `Experience "${res.data.data.label}" created!` });
      }
    } catch (err) {
      console.error("Failed to create experience:", err);
      setMessage({ type: "error", text: "Failed to create experience" });
    } finally {
      setModalLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate dates
    if (name === "startDate") {
      const today = getTodayDateString();
      if (value && value < today) {
        setDateErrors(prev => ({ ...prev, startDate: "Start date cannot be in the past" }));
      } else {
        setDateErrors(prev => ({ ...prev, startDate: "" }));
      }
      // Validate end date against new start date
      if (formData.endDate && value && formData.endDate < value) {
        setDateErrors(prev => ({ ...prev, endDate: "End date must be after start date" }));
      } else {
        setDateErrors(prev => ({ ...prev, endDate: "" }));
      }
    }

    if (name === "endDate") {
      if (formData.startDate && value && value < formData.startDate) {
        setDateErrors(prev => ({ ...prev, endDate: "End date must be after start date" }));
      } else {
        setDateErrors(prev => ({ ...prev, endDate: "" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    // Date validation
    const today = getTodayDateString();
    if (formData.startDate && formData.startDate < today) {
      setMessage({ type: "error", text: "Start date cannot be in the past" });
      setLoading(false);
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      setMessage({ type: "error", text: "End date must be after start date" });
      setLoading(false);
      return;
    }

    const id = getClientId();
    if (!id) {
      setMessage({ type: "error", text: "Client not authenticated. Please login again." });
      setLoading(false);
      return;
    }

    try {
      const submissionData = {
        ...formData,
        startTime: `${formData.startTime} ${formData.startPeriod}`,
        endTime: `${formData.endTime} ${formData.endPeriod}`,
        clientId: id,
      };
      const response = await axios.post(`${API_BASE_URL}/jobs/create`, submissionData);
      if (response.data.success) {
        setMessage({ type: "success", text: "Short job posted successfully!" });
        setTimeout(() => navigate("/posted-short-jobs"), 100);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to post job" });

    } finally {
      setLoading(false);




    }
  };

  return (
    <div style={{ backgroundColor: 'white' }} className="emp-dash">
      <main className="p-4">
        {/* Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Post <span>Short Job</span>
            </h1>
            <p className="emp-dash__subtitle">
              Create a short-term shift posting for recruitment.
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiBriefcase />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Status Message */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === "success"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                : "bg-rose-50 text-rose-600 border border-rose-100"
            }`}
          >
            {message.type === "success" ? (
              <FiCheckCircle size={18} />
            ) : (
              <FiX size={18} />
            )}
            <span className="text-sm font-bold">{message.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">

              {/* Job Info Card */}
              <div className="emp-dash__card p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <FiBriefcase size={15} className="text-indigo-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Job Details
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Job Position */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Job Position <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group" ref={roleRef}>
                      <FiBriefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors z-10" size={15} />
                      <input
                        type="text"
                        name="role"
                        value={formData.role}
                        onChange={(e) => { handleChange(e); setShowRoleDropdown(true); }}
                        onFocus={() => setShowRoleDropdown(true)}
                        onKeyPress={(e) => {
                          // Allow only letters, numbers, and space
                          if (!/[a-zA-Z0-9 ]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        required
                        autoComplete="off"
                        className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-colors outline-none text-sm text-gray-800 bg-white"
                        placeholder="e.g. ICU Nurse"
                      />
                      {showRoleDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar">
                          <button
                            type="button"
                            onClick={() => setShowRoleModal(true)}
                            className="w-full px-4 py-2.5 text-left text-xs font-bold text-indigo-600 hover:bg-indigo-50 border-b border-gray-100 flex items-center gap-2"
                          >
                            <FiPlus size={13} /> Create New Role
                          </button>
                          {roles.filter(r => r.name.toLowerCase().includes(formData.role.toLowerCase())).map(role => (
                            <div
                              key={role._id}
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer"
                              onClick={() => { setFormData(p => ({ ...p, role: role.name })); setShowRoleDropdown(false); }}
                            >
                              {role.name}
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-indigo-600 transition-colors cursor-pointer"
                        onClick={() => { setFormData(p => ({ ...p, role: '' })); setShowRoleDropdown(true); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Department */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Department <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group" ref={deptRef}>
                      <FiList className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors z-10" size={15} />
                      <input
                        type="text"
                        name="department"
                        value={formData.department}
                        onChange={(e) => { handleChange(e); setShowDeptDropdown(true); }}
                        onFocus={() => setShowDeptDropdown(true)}
                        required
                        autoComplete="off"
                        className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 bg-white"
                        placeholder="Enter or search department..."
                      />
                      {showDeptDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar">
                          <button
                            type="button"
                            onClick={() => setShowDeptModal(true)}
                            className="w-full px-4 py-2.5 text-left text-xs font-bold text-indigo-600 hover:bg-indigo-50 border-b border-gray-100 flex items-center gap-2"
                          >
                            <FiPlus size={13} /> Create New Department
                          </button>
                          {departments.filter(d => d.name.toLowerCase().includes(formData.department.toLowerCase())).map(dept => (
                            <div
                              key={dept._id}
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer"
                              onClick={() => { setFormData(p => ({ ...p, department: dept.name })); setShowDeptDropdown(false); }}
                            >
                              {dept.name}
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-indigo-600 transition-colors cursor-pointer"
                        onClick={() => { setFormData(p => ({ ...p, department: '' })); setShowDeptDropdown(true); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Salary */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Salary / Pay <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group">
                      <FaRupeeSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                      <input
                        type="text"
                        name="salary"
                        value={formData.salary}
                        onChange={handleChange}
                        required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                        placeholder="e.g. 2000 / Day"
                      />
                    </div>
                  </div>

                  {/* Experience */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Experience Required <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group" ref={expDropdownRef}>
                      <FiList className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors z-10" size={15} />
                      <input
                        type="text"
                        name="experience"
                        value={formData.experience}
                        onChange={(e) => { handleChange(e); setShowExpDropdown(true); }}
                        onFocus={() => setShowExpDropdown(true)}
                        required
                        autoComplete="off"
                        className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 bg-white"
                        placeholder="Enter or select experience..."
                      />
                      {showExpDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar">
                          <button
                            type="button"
                            onClick={() => setShowExpModal(true)}
                            className="w-full px-4 py-2.5 text-left text-xs font-bold text-indigo-600 hover:bg-indigo-50 border-b border-gray-100 flex items-center gap-2"
                          >
                            <FiPlus size={13} /> Create New Experience
                          </button>
                          {experienceOptions.filter(opt => opt.label.toLowerCase().includes((formData.experience || "").toLowerCase())).map((opt) => (
                            <div
                              key={opt._id}
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer"
                              onClick={() => { setFormData(p => ({ ...p, experience: opt.label })); setShowExpDropdown(false); }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-indigo-600 transition-colors cursor-pointer"
                        onClick={() => { setFormData(p => ({ ...p, experience: '' })); setShowExpDropdown(true); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Location <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group">
                      <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                        placeholder="e.g. Hyderabad"
                      />
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Required Skills <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group">
                      <FiFileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                      <input
                        type="text"
                        name="skills"
                        value={formData.skills}
                        onChange={handleChange}
                        required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                        placeholder="Nursing, Communication, Patient Care"
                      />
                    </div>
                    <p className="text-[10px] text-gray-500">Separate multiple skills with commas</p>
                  </div>
                </div>
              </div>

              {/* Shift Timing Card */}
              <div className="emp-dash__card p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FiClock size={15} className="text-blue-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Shift Timing
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Start Date */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Start Date <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        min={getTodayDateString()}
                        style={{ colorScheme: 'light' }}
                        className={`w-full pl-11 pr-4 py-3.5 rounded-xl border focus:ring-4 transition-all outline-none text-sm text-gray-800 bg-white ${
                          dateErrors.startDate ? 'border-rose-300 focus:ring-rose-600/5 focus:border-rose-600' : 'border-gray-200 focus:ring-indigo-600/5 focus:border-indigo-600'
                        }`}
                      />
                    </div>
                    {dateErrors.startDate && (
                      <p className="text-[10px] text-rose-600 font-medium mt-1">{dateErrors.startDate}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      End Date <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative group">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        required
                        min={formData.startDate || getTodayDateString()}
                        style={{ colorScheme: 'light' }}
                        className={`w-full pl-11 pr-4 py-3.5 rounded-xl border focus:ring-4 transition-all outline-none text-sm text-gray-800 bg-white ${
                          dateErrors.endDate ? 'border-rose-300 focus:ring-rose-600/5 focus:border-rose-600' : 'border-gray-200 focus:ring-indigo-600/5 focus:border-indigo-600'
                        }`}
                      />
                    </div>
                    {dateErrors.endDate && (
                      <p className="text-[10px] text-rose-600 font-medium mt-1">{dateErrors.endDate}</p>
                    )}
                  </div>

                  {/* Start Time */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Start Time <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1 group">
                        <FiClock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                        <input
                          type="time"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleChange}
                          required
                          className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                        />
                      </div>
                      <select
                        name="startPeriod"
                        value={formData.startPeriod}
                        onChange={handleChange}
                        className="px-3 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none text-sm font-bold text-gray-700"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>

                  {/* End Time */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      End Time <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1 group">
                        <FiClock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                        <input
                          type="time"
                          name="endTime"
                          value={formData.endTime}
                          onChange={handleChange}
                          required
                          className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                        />
                      </div>
                      <select
                        name="endPeriod"
                        value={formData.endPeriod}
                        onChange={handleChange}
                        className="px-3 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none text-sm font-bold text-gray-700"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>

                  {/* Total Duration */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Total Duration
                    </label>
                    <div className="flex items-center gap-2.5 py-3.5 px-4 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-bold">
                      <FiZap size={15} className="text-emerald-500" />
                      <span>{formData.totalDuration || "Auto-calculated from times above"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Description Card */}
              <div className="emp-dash__card p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FiList size={15} className="text-blue-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Job Description
                  </h2>
                </div>
                <div className="relative group">
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={7}
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 resize-none"
                    placeholder="Detail the shift responsibilities, dress code, patient ratios, or onboarding requirements..."
                  />
                </div>
              </div>

              {/* Application Deadline Card */}
              <div className="emp-dash__card p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <FiClipboard size={15} className="text-amber-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Application Deadline
                  </h2>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                      Expiry Date :
                    </label>
                    <span className="text-xs font-bold text-gray-500">dd-mm-yy</span>
                  </div>
                  <div className="relative group">
                    <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-amber-600 transition-colors" size={15} />
                    <input
                      type="date"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      min={getTodayDateString()}
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-amber-600/5 focus:border-amber-600 transition-all outline-none text-sm text-gray-800"
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 italic">Applications will be disabled after this date.</p>
                </div>
              </div>

              {/* Company Info Card */}
              <div className="emp-dash__card p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                      <FiBriefcase size={15} className="text-violet-600" />
                    </div>
                    <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Company Info</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCompanyInfo(prev => !prev)}
                      className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${
                        showCompanyInfo
                          ? "bg-violet-50 text-violet-600 border-violet-100 hover:bg-violet-100"
                          : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200"
                      }`}
                    >
                      {showCompanyInfo ? "Hide" : "Show"}
                    </button>
                    {showCompanyInfo && (
                      <button
                        type="button"
                        onClick={() => setEditingCompany(prev => !prev)}
                        className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${
                          editingCompany
                            ? "bg-violet-600 text-white border-violet-600"
                            : "bg-violet-50 text-violet-600 border-violet-100 hover:bg-violet-100"
                        }`}
                      >
                        {editingCompany ? <FiCheckCircle size={12} /> : <FiZap size={12} />}
                        {editingCompany ? "Done" : "Edit"}
                      </button>
                    )}
                  </div>
                </div>

                {showCompanyInfo && (
                  <>
                    {editingCompany ? (
                      <div className="space-y-4">
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">Company Name</label>
                          <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-4 focus:ring-violet-600/5 transition-all outline-none text-sm font-medium"
                            placeholder="e.g. Timely Health"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">About Company</label>
                          <textarea
                            name="aboutCompany"
                            value={formData.aboutCompany}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-4 focus:ring-violet-600/5 transition-all outline-none text-sm font-medium resize-none"
                            placeholder="Brief description of your company, mission, and culture..."
                          />
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const id = getClientId();
                              await axios.put(`${API_BASE_URL}/admin/settings/company`, {
                                clientId: id,
                                companyName: formData.companyName,
                                aboutCompany: formData.aboutCompany,
                              });
                              setEditingCompany(false);
                            } catch (err) {
                              console.error("Failed to save company defaults", err);
                            }
                          }}
                          className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-widest transition-all shadow-lg shadow-violet-600/20"
                        >
                          Save as Default
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="p-3 bg-violet-50/60 rounded-xl border border-violet-100">
                          <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1">Company Name</p>
                          <p className="text-sm font-bold text-gray-800">{formData.companyName || "Timely Health"}</p>
                        </div>
                        {formData.aboutCompany && (
                          <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1">About</p>
                            <p className="text-xs text-gray-700 leading-relaxed">{formData.aboutCompany}</p>
                          </div>
                        )}
                        <p className="text-[9px] text-gray-500 italic">Click Edit to customise for this job post or update defaults.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right Column – Actions */}
            <div className="space-y-6">
              {/* Action Buttons Card */}
              <div className="emp-dash__card p-6 space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-indigo-600/20"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <FiSend size={15} />
                      Post Short Job
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all"
                >
                  <FiX size={15} />
                  Cancel
                </button>

                {/* Quick Tip */}
                <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-2">
                  <FiZap size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-amber-800 font-medium">
                    Short jobs are ideal for temporary or shift-based positions with fixed start and end times.
                  </p>
                </div>
              </div>

              {/* Shift Summary Card */}
              <div className="emp-dash__card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <FiClipboard size={15} className="text-emerald-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Shift Summary
                  </h2>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Role", value: formData.role, icon: <FiBriefcase size={13} className="text-indigo-400" /> },
                    { label: "Location", value: formData.location, icon: <FiMapPin size={13} className="text-rose-400" /> },
                    { label: "Duration", value: formData.totalDuration, icon: <FiZap size={13} className="text-amber-400" /> },
                    { label: "Salary", value: formData.salary, icon: <FaRupeeSign size={12} className="text-emerald-500" /> },
                  ].map(({ label, value, icon }) => (
                    <div key={label} className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                      {icon}
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{label}</p>
                        <p className="text-xs font-semibold text-gray-700 truncate">{value || "—"}</p>
                      </div>
                    </div>
                  ))}
                  {formData.startDate && formData.endDate && (
                    <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                      <FiCalendar size={13} className="text-blue-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Period</p>
                        <p className="text-xs font-semibold text-gray-700">
                          {formData.startDate.split("-").reverse().join("/")} – {formData.endDate.split("-").reverse().join("/")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Create Role Modal */}
        {showRoleModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-base font-bold text-gray-800">Create New Role</h3>
                <button onClick={() => setShowRoleModal(false)} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-xl transition-all"><FiX size={18} /></button>
              </div>
              <form onSubmit={handleCreateRole} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Role Name</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm(p => ({ ...p, name: e.target.value }))}
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm"
                    placeholder="e.g. Senior Nurse"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Description</label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) => setRoleForm(p => ({ ...p, description: e.target.value }))}
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm resize-none"
                    rows="3"
                    placeholder="Role description..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-60"
                >
                  {modalLoading ? "Creating..." : "Create Role"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Create Department Modal */}
        {showDeptModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-base font-bold text-gray-800">Create New Department</h3>
                <button onClick={() => setShowDeptModal(false)} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-xl transition-all"><FiX size={18} /></button>
              </div>
              <form onSubmit={handleCreateDepartment} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Department Name</label>
                  <input
                    type="text"
                    value={deptForm.name}
                    onChange={(e) => setDeptForm(p => ({ ...p, name: e.target.value }))}
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm"
                    placeholder="e.g. Cardiology"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Description</label>
                  <textarea
                    value={deptForm.description}
                    onChange={(e) => setDeptForm(p => ({ ...p, description: e.target.value }))}
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm resize-none"
                    rows="3"
                    placeholder="Department description..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-60"
                >
                  {modalLoading ? "Creating..." : "Create Department"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Create Experience Modal */}
        {showExpModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-base font-bold text-gray-800">Create Experience Level</h3>
                <button onClick={() => setShowExpModal(false)} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-xl transition-all"><FiX size={18} /></button>
              </div>
              <form onSubmit={handleCreateExperience} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Experience Label</label>
                  <input
                    type="text"
                    value={newExpLabel}
                    onChange={(e) => setNewExpLabel(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm"
                    placeholder="e.g. 2-3 Years"
                  />
                </div>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-60"
                >
                  {modalLoading ? "Creating..." : "Create Experience"}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}



export default ShortJobs;